<!-- eslint-disable vue/no-v-html -->
<template>
  <div :class="props.class">
    <div
      v-if="!imageBase64"
      class="flex items-center justify-center h-full -mt-2"
    >
      <Icon name="circle-notch animate-spin" />
    </div>
    <img
      v-if="imageBase64"
      :src="imageBase64"
      class="w-full h-full object-contain"
    />
    <div class="pointer-events-none absolute opacity-0 hidden">
      <div :id="`pixi-text-${instanceId}`" class="overflow-hidden opacity-0">
        <span
          v-for="(char, index) in metadata?.text"
          :key="index"
          class="font-['Font_Awesome_6_Free']"
        >
          <i
            v-if="char && char?.length > 1"
            class="fa-solid"
            v-html="`${'&'}#x${char};`"
          />
          <i v-else>
            {{ char || ' ' }}
          </i>
        </span>
      </div>
      <div :id="`pixi-canvas-${instanceId}`" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Input } from '@pixi/ui'
import { useWindowSize } from '@vueuse/core'
import { find, includes, isEmpty, size, toNumber } from 'lodash-es'
import type { Application, Container, Renderer, Sprite, Text } from 'pixi.js'
import { applyTextGradient } from '~/utils/pixi/personalizationUtil'

const imageBase64 = ref<string | null>(null)
const firstRender = ref(false)
const emits = defineEmits(['change'])

const props = defineProps<{
  renderFor?: 'printing' | 'preview'
  class?: string
  metadata?: {
    artworkBackgroundUrl?: string | null
    textStyle?: string // Misto, HouseSlant
    textPosition?: string // center, left, right
    textColors?: { colors: string[]; rotation: number }
    text?: (string | undefined | null)[]
    deviceList?: GetDeviceAllResponse['data']
    personalization?: {
      background_color: {
        hex: string
        image: string
        cmyk: {
          c: string
          m: string
          y: string
          k: string
        }
      }
      stickers: {
        id: string
        x: number
        y: number
        width: number
        height: number
        image: string
        rotation: number
      }[]
      device_color: { value: string; label: string }
      texts: {
        id: string
        x: number
        y: number
        width?: number
        height?: number
        text: string
        rotation: number
        color: string
        font_type: string
        font_size: number
        custom_color: string
        position_type: string
      }[]
      photos: {
        id: string
        x: number
        y: number
        width: number
        height: number
        type: string
        url: string
        aspectRatio: number
        rotation: number
      }[]
      case: {
        slug: string
        name: string
        product_variant_detail_id: string
      }
      device: {
        label: string
        value: string
        slug: string
        category: string
        category_slug: string
        device_image: string
        case_image: string
        mark_image: string
        color: string
      }
    }
  }
}>()

const config = useRuntimeConfig()
const instanceId = useId()
const { width } = useWindowSize()

// Pixi Scale
const maxWidth = isEmpty(props?.metadata?.personalization) ? 300 : 350
const maxHeight =
  width.value < 768 && !props.metadata?.personalization ? 300 : 533 - 20 - 20

const app: Application<Renderer> = new PIXI.Application()
const text: Text = new PIXI.Text({
  text: 'loading...',
  resolution: window?.devicePixelRatio * 4,
  style: {
    lineHeight: 32,
    fontFamily: ['Supermarket', 'Font Awesome 6 Free'],
    padding: 1
  }
})

const handleUpdateBackground = async () => {
  try {
    const background = props.metadata?.artworkBackgroundUrl

    if (background) {
      const templateTexture = await PIXI.Assets.load(
        config.public.mediaUrl + '/' + background
      )

      const templateBackground = new PIXI.Sprite(templateTexture)

      const textureOriginalWidth = templateBackground.texture.width
      const textureOriginalHeight = templateBackground.texture.height

      const scaleFactor = maxHeight / textureOriginalHeight

      app.renderer.resize(
        textureOriginalWidth * scaleFactor,
        textureOriginalHeight * scaleFactor
      )

      templateBackground.position.set(
        toNumber(app?.screen?.width) / 2,
        toNumber(app?.screen?.height) / 2
      )
      templateBackground.anchor.set(0.5)
      templateBackground.scale.set(
        Math.min(
          toNumber(app?.screen?.width) / textureOriginalWidth,
          toNumber(app?.screen?.height) / textureOriginalHeight
        )
      )
      templateBackground.cullable = true
      app.stage.addChild(templateBackground)
    }
  } catch (error) {
    console.log(error)
  }
}

type TContainerCase = Container & { id?: string }
type TCustomSprite = Sprite & { id?: string; image?: string }
type TCustomInput = Input & { id?: string }

const backgroundColorCase = new PIXI.Graphics()
const backgroundCaseSprite = new PIXI.Sprite()
const caseSprite = new PIXI.Sprite()
const deviceSprite = new PIXI.Sprite()
const markSprite = new PIXI.Sprite()
const containerCase: TContainerCase = new PIXI.Container()
const containerSticker: TContainerCase = new PIXI.Container()
const containerPhoto: TContainerCase = new PIXI.Container()
const containerText: TContainerCase = new PIXI.Container()

const handlePrepareCustomUi = async () => {
  try {
    let device_image = ''
    let case_image = ''
    let mark_image = ''
    const personalization = props.metadata?.personalization
    const deviceList = props.metadata?.deviceList
    if (size(deviceList) > 0) {
      const categoryResult = find(
        deviceList,
        (item) => item.name === personalization?.device?.category
      )
      const iphone16group = [
        'iphone-16-pro-max',
        'iphone-16-pro',
        'iphone-16',
        'iphone-16-plus'
      ]
      if (!categoryResult) return
      const result = find(categoryResult?.devices, (item) => {
        if (
          includes(iphone16group, personalization?.device?.slug) &&
          (personalization?.device?.color === 'White' ||
            personalization?.device?.color === 'Pink' ||
            personalization?.device?.color === 'Standard')
        ) {
          if (personalization?.device?.slug === 'iphone-16-plus') {
            return (
              item.slug === personalization?.device?.slug &&
              (item.personalize_product_detail.color === 'Pink' ||
                item.personalize_product_detail.color === 'Standard') &&
              item.product_slug === personalization?.case?.slug
            )
          }
          return (
            item.slug === personalization?.device?.slug &&
            (item.personalize_product_detail.color === 'Standard' ||
              item.personalize_product_detail.color === 'White') &&
            item.product_slug === personalization?.case?.slug
          )
        }

        return (
          item.slug === personalization?.device?.slug &&
          item.personalize_product_detail.color ===
            personalization?.device?.color &&
          item.product_slug === personalization?.case?.slug &&
          personalization?.case?.product_variant_detail_id === item?.item_id
        )
      })
      if (!result) return
      device_image =
        config.public.mediaUrl +
        '/' +
        result.personalize_product_detail.background_image
      case_image =
        config.public.mediaUrl +
        '/' +
        result.personalize_product_detail.case_image
      mark_image =
        config.public.mediaUrl +
        '/' +
        result.personalize_product_detail.background_logo
    } else {
      device_image = props.metadata?.personalization?.device?.device_image || ''
      case_image = props.metadata?.personalization?.device?.case_image || ''
      mark_image = props.metadata?.personalization?.device?.mark_image || ''
    }
    deviceSprite.texture = await PIXI.Assets.load(device_image)
    caseSprite.texture = await PIXI.Assets.load(case_image)
    markSprite.texture = await PIXI.Assets.load(mark_image)

    markSprite.zIndex = 100
    app.stage.sortableChildren = true

    deviceSprite.anchor.set(0.5)
    deviceSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / deviceSprite.texture.width,
        toNumber(maxHeight) / deviceSprite.texture.height
      )
    )
    deviceSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )
    markSprite.eventMode = 'none'
    markSprite.anchor.set(0.5)
    markSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / markSprite.texture.width,
        toNumber(maxHeight) / markSprite.texture.height
      )
    )
    markSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )
    caseSprite.anchor.set(0.5)
    caseSprite.scale.set(
      Math.min(
        toNumber(maxWidth) / caseSprite.texture.width,
        toNumber(maxHeight) / caseSprite.texture.height
      )
    )
    caseSprite.position.set(
      toNumber(maxWidth * 3) / 2,
      toNumber(maxHeight * 2) / 2
    )

    containerCase.mask = caseSprite

    containerSticker.zIndex = 3
    containerPhoto.zIndex = 2
    containerText.zIndex = 3

    containerCase.cullable = true
    containerSticker.cullable = true
    containerPhoto.cullable = true
    containerText.cullable = true
    containerCase.addChild(
      caseSprite,
      backgroundCaseSprite,
      backgroundColorCase,
      containerSticker,
      containerPhoto,
      containerText,
      markSprite
    )
    containerCase.sortableChildren = true
    app.stage.addChild(deviceSprite, containerCase)
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateBackgroundCaseColor = async () => {
  try {
    const background_color = props.metadata?.personalization?.background_color
    backgroundColorCase.rect(0, 0, 1000, 1000)
    !(background_color?.hex || background_color?.cmyk?.c) &&
      backgroundColorCase.fill('#000000')
    backgroundColorCase.visible = true
    backgroundColorCase.alpha = 0
    backgroundCaseSprite.visible = false

    if (background_color?.image) {
      backgroundColorCase.visible = false
      backgroundCaseSprite.visible = true
      backgroundColorCase.alpha = 0
      backgroundCaseSprite.texture = await PIXI.Assets.load(
        background_color?.image
      )
      backgroundCaseSprite.position.set(
        toNumber(maxWidth * 3) / 2,
        toNumber(maxHeight * 2) / 2
      )
      backgroundCaseSprite.scale.set(
        Math.max(
          toNumber(maxWidth) / backgroundCaseSprite.texture.width,
          toNumber(maxHeight) / backgroundCaseSprite.texture.height
        )
      )
      backgroundCaseSprite.anchor.set(0.5)
    } else if (background_color?.hex || background_color?.cmyk?.c) {
      backgroundCaseSprite.visible = false
      backgroundColorCase.visible = true
      backgroundColorCase.alpha = 1
      backgroundColorCase.fill(
        background_color?.cmyk?.c
          ? cmyk2rgb({
              c: toNumber(background_color?.cmyk?.c),
              m: toNumber(background_color?.cmyk?.m),
              y: toNumber(background_color?.cmyk?.y),
              k: toNumber(background_color?.cmyk?.k)
            })
          : background_color?.hex
      )
      backgroundCaseSprite.texture = await PIXI.Assets.load('')
    }
  } catch (error) {
    console.log(error)
  }
}

const handlePrepareStickerUi = async () => {
  try {
    for await (const item of props.metadata?.personalization?.stickers || []) {
      const texture = await PIXI.Assets.load({
        src: item.image
      })
      const stickerSprite: TCustomSprite = new PIXI.Sprite(texture)
      stickerSprite.anchor = 0.5
      stickerSprite.width = item.width
      stickerSprite.height = item.height
      stickerSprite.x = item.x
      stickerSprite.y = item.y
      stickerSprite.id = item.id
      stickerSprite.rotation = item.rotation
      stickerSprite.image = item.image
      stickerSprite.cullable = true
      stickerSprite.eventMode = 'static'

      containerSticker.addChild(stickerSprite)
    }
  } catch (error) {
    console.log(error)
  }
}

const handlePreparePhotoUi = async () => {
  try {
    for await (const item of props.metadata?.personalization?.photos || []) {
      const texture = await PIXI.Assets.load(item.url)
      const photoSprite: TCustomSprite = new PIXI.Sprite(texture)
      photoSprite.anchor = 0.5
      photoSprite.width = item.width
      photoSprite.height = item.height
      photoSprite.x = item.x
      photoSprite.y = item.y
      photoSprite.id = item.id
      photoSprite.cullable = true
      photoSprite.eventMode = 'static'
      photoSprite.rotation = toNumber(item.rotation)

      containerPhoto.addChild(photoSprite)
    }
  } catch (error) {
    console.log(error)
  }
}

const handlePrepareTextCustomUi = async () => {
  try {
    const borderColor = '#DCB000'
    const backgroundColor = '#F1D583'
    for await (const item of props.metadata?.personalization?.texts || []) {
      try {
        if (!item?.text) return
        const inputWidth = item.width || 0
        const inputHeight = item.height || 0
        const input: TCustomInput = new PIXIUI.Input({
          bg: new PIXI.Graphics()
            .fill({ color: borderColor, alpha: 0 })
            .roundRect(0, 0, inputWidth, inputHeight, 0)
            .fill({ color: backgroundColor, alpha: 0 })
            .roundRect(0, 0, inputWidth, inputHeight, 0),
          nineSliceSprite: [100, 0, 100, 0],
          align:
            item.position_type === 'bottom-left'
              ? 'left'
              : item.position_type === 'bottom-right'
                ? 'right'
                : 'center',
          textStyle: {
            fontFamily: [item.font_type, 'Supermarket', 'Font Awesome 6 Free'],
            fontSize: `${item.font_size}px`,
            fill: item.custom_color || item.color,
            lineHeight: 66
          },
          padding: {
            left: 5
          },
          value: item.text || '',
          placeholder: 'Enter ...'
        })
        input.rotation = item.rotation
        input.id = item.id
        input.pivot.set(toNumber(item.width) / 2, toNumber(item.height) / 2)
        input.x = item.x
        input.y = item.y
        input.scale = 1
        containerText.addChild(input)
      } catch (error) {
        console.log(error)
      }
    }
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateCustomPersonalization = async () => {
  await Promise.all([
    handlePrepareCustomUi(),
    handleUpdateBackgroundCaseColor(),
    handlePrepareStickerUi(),
    handlePreparePhotoUi(),
    handlePrepareTextCustomUi()
  ])
}

const handleUpdateTextInstance = () => {
  try {
    text.cullable = true
    app.stage.addChild(text)
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateText = async () => {
  try {
    const textProps = props.metadata?.text?.join('')
    if (textProps) {
      const textValue = (
        document.getElementById(`pixi-text-${instanceId}`)?.innerText || ''
      ).replace(/(\r\n|\n|\r)/gm, '')
      text.text = textValue
      text.position.set(app.stage.width / 2, app.stage.height / 2)
      text.anchor.set(0.5)
    } else {
      text.text = ''
    }
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateTextFontFamily = () => {
  try {
    const fontFamily = props.metadata?.textStyle
    if (fontFamily) {
      text.style.fontFamily = [fontFamily, 'Supermarket', 'Font Awesome 6 Free']
      if (app.renderer) {
        app.renderer.render(app.stage)
      }
    }
  } catch (error) {
    console.log(error)
  }
}

const handleUpdateTextFit = () => {
  const position = props.metadata?.textPosition || 'center'

  const safeArea = {
    center: app.stage.width * 0.808,
    left: app.stage.height * 0.519,
    right: app.stage.height * 0.519
  }

  const maxHeight = {
    center: app.stage.width * 0.3,
    left: app.stage.width * 0.3,
    right: app.stage.width * 0.3
  }

  const fx = text.width / text.height

  if (fx) {
    const width = safeArea[position as keyof typeof safeArea]
    const height = safeArea[position as keyof typeof safeArea] / fx

    text.width = width
    text.height = height

    const scaleFactor = width / height

    const newHeight = Math.min(
      height,
      safeArea[position as keyof typeof safeArea],
      maxHeight[position as keyof typeof maxHeight]
    )

    text.width = newHeight * scaleFactor
    text.height = newHeight
  }
}

const handleUpdateTextPosition = () => {
  const position = props.metadata?.textPosition || 'center'

  const safeArea = {
    center: app.stage.width * 0.025,
    left: app.stage.height * 0.025,
    right: app.stage.height * 0.025
  }

  if (!!position && !!safeArea[position as keyof typeof safeArea]) {
    if (position === 'center') {
      text.rotation = 0
      text.position.set(app.stage.width / 2, app.stage.height / 2)
      text.anchor.set(0.5)
    }

    if (position == 'left') {
      text.rotation = Math.PI / 2
      text.position.set(
        text.height + app.stage.height * 0.025 + safeArea[position],
        app.stage.height - app.stage.height * 0.06
      )
      text.anchor.set(1, 0)
    }

    if (position == 'right') {
      text.rotation = -(Math.PI / 2)
      text.position.set(
        app.stage.width -
          text.height / 2 -
          app.stage.height * 0.025 -
          safeArea[position],
        app.stage.height - app.stage.height * 0.06
      )
      text.anchor.set(0, 0.5)
    }
  }
}

onMounted(async () => {
  try {
    // Initial PIXI Instance
    await app.init({
      backgroundAlpha: 0,
      width: props.metadata?.personalization ? maxWidth * 3 : maxWidth,
      height: props.metadata?.personalization ? maxHeight * 2 : maxHeight,
      roundPixels: true,
      resolution: 0.5,
      autoDensity: true,
      antialias: false,
      preserveDrawingBuffer: true,
      preference: 'webgl',
      webgl: { powerPreference: 'low-power' },
      powerPreference: 'low-power'
    })
    app.stage.cullable = true
    app.stage.cullableChildren = true
    document
      .getElementById(`pixi-canvas-${instanceId}`)
      ?.appendChild(app.canvas)

    // Update PIXI Background
    await handleUpdateBackground()
    //   Append Text to Stage
    handleUpdateTextInstance()
    handleUpdateText()
    handleUpdateTextFontFamily()

    props.metadata?.personalization
      ? await handleUpdateCustomPersonalization()
      : app.ticker.add(async () => {
          handleUpdateText()
          handleUpdateTextFontFamily()
          handleUpdateTextFit()
          handleUpdateTextPosition()
          props.metadata?.textColors &&
            applyTextGradient(app, text, {
              colors: props.metadata?.textColors.colors,
              rotation: props.metadata?.textColors.rotation
            })
        })
    if (app?.renderer) {
      const interval = setInterval(async () => {
        if (firstRender.value) {
          clearInterval(interval)
          return
        }
        if (app?.renderer) {
          const scaleFactor = Math.min(
            toNumber(maxWidth) / deviceSprite.texture.width,
            toNumber(maxHeight) / deviceSprite.texture.height
          )
          const base64 = await app?.renderer?.extract.image({
            target: app.stage,
            quality: 1,
            resolution: 1,
            antialias: false,
            ...(!isEmpty(props.metadata?.personalization)
              ? {
                  frame: new PIXI.Rectangle(
                    (maxWidth * 3) / 2 -
                      (deviceSprite.texture.width / 2) * scaleFactor -
                      5,
                    (maxHeight * 2) / 2 -
                      (deviceSprite.texture.height / 2) * scaleFactor -
                      5,
                    deviceSprite.texture.width * scaleFactor + 10,
                    deviceSprite.texture.height * scaleFactor + 10
                  )
                }
              : {})
          })
          imageBase64.value = base64.src
          emits('change', base64.src)
          app?.ticker?.stop()
          firstRender.value = true
        }
      }, 1000)
    }
  } catch (error) {
    console.log(error)
  }
})

onBeforeUnmount(async () => {
  try {
    await PIXI.Assets.reset()
    await PIXI.Cache.reset()
    app?.destroy()
  } catch (error) {
    console.log(error)
  }
})
</script>
